import React, { useEffect } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Typography,
  Paper,
  CircularProgress,
  Tooltip,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import { useDispatch, useSelector } from "react-redux";
import { getAdditionalDetails, getBusinessesList, getCarrierList, getMarketPlaceList, getProductTypeList, saveAdditionalDetails } from "../../store/actions/userOnboarding";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import DeleteIcon from '@material-ui/icons/Delete';
import { v4 as uuidv4 } from 'uuid';
const useStyles = makeStyles((theme) => ({
    notSoLight: {
        color: "#474646 !important",
        WebkitTextFillColor: "#474646 !important", // Important for Chrome
        opacity: 1
    }
}));
export default function AdditionalInformation(props) {
    const classes = useStyles();
    const {isViewUserRequest, activeStep, handleBack, handleNext, data, isApprovedSeller} = props;
    const [showLoader, setShowLoader] = React.useState(false);
    const [originalCopy, setOriginalCopy] = React.useState(null);
    const dispatch = useDispatch();
    const { businessList, productTypeList, marketplaceList, carrierList } = useSelector((state) => state.userOnboarding);
    // Local option states to avoid mutating Redux state or module-level arrays
    const [businessOptions, setBusinessOptions] = React.useState([]);
    const [productTypeOptions, setProductTypeOptions] = React.useState([]);
    const [marketplaceOptions, setMarketplaceOptions] = React.useState([]);
    const [carrierOptions, setCarrierOptions] = React.useState([]);
    
    useEffect(() => {
        getAdditionDetails();
    }, []);

    const getAdditionDetails = async () => {
        setShowLoader(true);
        let businessResponse = await getAdditionalDetails(isViewUserRequest ? data?.Id : null, isApprovedSeller);
        if (businessResponse?.data?.Status === 200) {
            // Assign unique IDs to each store in the fetched data
            let storesWithIds = businessResponse.data.Entity.storeList.map(store => ({
                ...store,
                storeIndexId: uuidv4()
            }));
            setOriginalCopy(JSON.parse(JSON.stringify({
                ...businessResponse.data.Entity,
                storeList: storesWithIds
            })));

            setFormData({
                ...businessResponse.data.Entity,
                storeList: storesWithIds
            });
        }
        setShowLoader(false);
    };

    useEffect(() => {
        // Map fetched business list to the format needed for dropdown
        if (businessList && businessList.length > 0) {
            setBusinessOptions([...businessList]);
        }
        // Map fetched product types to the format needed for dropdown
        if (productTypeList && productTypeList.length > 0) {
            setProductTypeOptions([...productTypeList]);
        }
        // Map fetched marketplaces to the format needed for dropdown
        if (marketplaceList && marketplaceList.length > 0) {
            setMarketplaceOptions([...marketplaceList]);
        }
        // Map fetched carriers to the format needed for dropdown
        if (carrierList && carrierList.length > 0) {
            setCarrierOptions([...carrierList]);
        }
    }, [businessList, productTypeList, marketplaceList, carrierList]);

    useEffect(() => {
        if (!businessList.length) {
            dispatch(getBusinessesList(isApprovedSeller));
        }
    }, [businessList, dispatch, isApprovedSeller]);

    useEffect(() => {
        if (!productTypeList.length) {
            dispatch(getProductTypeList(isApprovedSeller));
        }
    }, [productTypeList, dispatch, isApprovedSeller]);
    
    useEffect(() => {
        if (!marketplaceList.length) {
            dispatch(getMarketPlaceList(isApprovedSeller));
        }
    }, [marketplaceList, dispatch, isApprovedSeller]);
    
    useEffect(() => {
        if (!carrierList.length) {
            dispatch(getCarrierList(isApprovedSeller));
        }
    }, [carrierList, dispatch, isApprovedSeller]);

    const [formData, setFormData] = React.useState({
        businessModelIds: [],
        productTypeIds: [],
        averageOrdersPerDay: "",
        averageSkuCount: "",
        otherInformation: "",
        preferredCarrierIds: [],
        marketplaceIds: [],
        storeList: [
            {sellerStoreName: "", websiteUrl: "", sellerLogoUrl: "", storeIndexId: uuidv4()}
        ]
    });

    const [errors, setErrors] = React.useState({});

    const hasExistingValue = (value) => {
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        if (value === null || value === undefined) {
            return false;
        }
        return String(value).trim() !== "";
    };

    const isLockedField = (fieldName) => {
        if (!isApprovedSeller) {
            return false;
        }
        return hasExistingValue(originalCopy?.[fieldName]);
    };

    const isExistingStore = (store) => {
        if (!isApprovedSeller || !originalCopy?.storeList?.length) {
            return false;
        }
        return originalCopy.storeList.some((item) => item.storeIndexId === store.storeIndexId);
    };

    const handleMultiModelChange = (event) => {
        const { name } = event.target;
        if (isLockedField(name)) {
            return;
        }
        const value = event.target.value || [];
        if (name === "businessModelIds") {
            setFormData((prev) => ({ ...prev, otherBusinessModel: value.includes(6) ? prev.otherBusinessModel : "" }));
        } else if (name === "productTypeIds") {
            setFormData((prev) => ({ ...prev, otherProductType: value.includes(10) ? prev.otherProductType : "" }));
        }
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const validateField = (name, value) => {
        if (name === "businessModelIds") {
            if (!value || value.length === 0) return "Select at least one business model";
            return "";
        }
        if (name === "productTypeIds") {
            if (!value || value.length === 0) return "Select at least one product type";
            return "";
        }
        if (name === "storeList") {
            const hasAtLeastOneStore = formData.storeList.some(
                store => store.sellerStoreName?.trim()
            );

            const hasInvalidStore = formData.storeList.some(store =>
                (store.websiteUrl?.trim() || store.sellerLogoUrl?.trim()) &&
                !store.sellerStoreName?.trim()
            );

            //validate if website URL and logo URL are in correct format when they are provided
            const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w\-\.~:/?#[\]@!$&'()*+,;=]*)?$/; //NOSONAR

            let finalError = ""; 
            formData.storeList.forEach(store => {
                if (store.websiteUrl?.trim() && !urlPattern.test(store.websiteUrl.trim())) {
                    finalError = "Invalid website URL format";
                }
                if (store.sellerLogoUrl?.trim() && !urlPattern.test(store.sellerLogoUrl.trim())) {
                    finalError = "Invalid logo URL";
                }
            });

            if (finalError) {
                return finalError;
            }

            if (!hasAtLeastOneStore) {
                return "At least one Seller Store Name is required";
            }

            if (hasInvalidStore) {
                return "Seller Store Name is required when Website URL or Logo is provided";
            }
        }
        return "";
    };

    const validateAll = () => {
        const nextErrors = {};
        Object.keys(formData).forEach((key) => {
            const err = validateField(key, formData[key]);
            if (err) nextErrors[key] = err;
        });
        setErrors(nextErrors);
        Object.keys(nextErrors).length > 0 && toast.error("Please fix validation errors before proceeding.");
        return Object.keys(nextErrors).length === 0;
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        const err = validateField(name, formData[name]);
        setErrors((prev) => ({ ...prev, [name]: err }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (isLockedField(name)) {
            return;
        }
        if (name === "averageOrdersPerDay" || name === "averageSkuCount") {
            let newValue = value.replace(/^0+(?=\d)/, "");
            setFormData((prev) => ({ ...prev, [name]: newValue }));
            setErrors((prev) => ({ ...prev, [name]: '' }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSaveAndNext = () => {
        if (originalCopy && JSON.stringify(formData) === JSON.stringify(originalCopy)) {
            handleNext();
        } else if (validateAll() && JSON.stringify(formData) !== JSON.stringify(originalCopy)) {
            handleSaveForm();
        }
    };

    const handleSaveForm = async() => {
        setShowLoader(true);
        let payload = {
            ...formData,
            onboardingId: isViewUserRequest ? data?.Id : 0
        }
        let response = await saveAdditionalDetails(payload, isApprovedSeller);
        setShowLoader(false);
        if (response?.data?.Status === 200) {
            handleNext();
            toast.success("Additional details saved successfully.");
        } else {
            toast.error("Failed to save additional details. Please try again.");
        }
    };

    const handleStoreChange = (e, index) => {
        if (isExistingStore(formData.storeList[index])) {
            return;
        }
        const { name, value } = e.target;
        const updatedStoreList = [...formData.storeList];
        updatedStoreList[index][name] = value;
        setFormData((prev) => ({ ...prev, storeList: updatedStoreList }));
        setErrors((prev) => ({ ...prev, storeList: '' }));
    }

    const handleAddNewStore = () => {
        let newStoreList = formData.storeList ? [...formData.storeList] : [];
        newStoreList.push({sellerStoreName: "", websiteUrl: "", sellerLogoUrl: "", storeIndexId: uuidv4()});
        setFormData((prev) => ({
            ...prev,
            storeList: newStoreList
        }));
    };

    const handleDeleteStore = (index) => {
        if (isExistingStore(formData.storeList[index])) {
            return;
        }
        let newStoreList = formData.storeList ? [...formData.storeList] : [];
        newStoreList.splice(index, 1);
        setFormData((prev) => ({
            ...prev,
            storeList: newStoreList
        }));
    };

    return (
        <>
            <Paper className='onboardingFormWrapping fw-3' elevation={2} square>
                <Typography variant="h6" gutterBottom>
                    Additional Details
                </Typography>

                {showLoader && (
                    <div className="full-screen-spinner-overlay">
                        <CircularProgress disableShrink />
                    </div>
                )}

                <Grid container spacing={2}>
                    {/* Left Column */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            label="Select Business Model/s"
                            variant="outlined"
                            margin="normal"
                            name="businessModelIds"
                            value={formData.businessModelIds || []}
                            onChange={handleMultiModelChange}
                            onBlur={handleBlur}
                            error={!!errors.businessModelIds}
                            helperText={errors.businessModelIds}
                            disabled={isLockedField("businessModelIds")}
                            SelectProps={{ multiple: true }}
                            InputProps={{
                                classes: {
                                    disabled: classes.notSoLight
                                }
                            }}
                        >
                            {businessOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            label="Select Product Type/s"
                            variant="outlined"
                            margin="normal"
                            name="productTypeIds"
                            value={formData.productTypeIds || []}
                            onChange={handleMultiModelChange}
                            onBlur={handleBlur}
                            SelectProps={{ multiple: true }}
                            error={!!errors.productTypeIds}
                            helperText={errors.productTypeIds}
                            disabled={isLockedField("productTypeIds")}
                            InputProps={{
                                classes: {
                                    disabled: classes.notSoLight
                                }
                            }}
                        >
                            {productTypeOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            label="Preferred Carriers"
                            variant="outlined"
                            margin="normal"
                            name="preferredCarrierIds"
                            value={formData.preferredCarrierIds || []}
                            onChange={handleMultiModelChange}
                            onBlur={handleBlur}
                            SelectProps={{ multiple: true }}
                            error={!!errors.preferredCarrierIds}
                            helperText={errors.preferredCarrierIds}
                            disabled={isLockedField("preferredCarrierIds")}
                            InputProps={{
                                classes: {
                                    disabled: classes.notSoLight
                                }
                            }}
                        >
                            {carrierOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.carrierName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    
                    {/* Business Other */}
                    {formData?.businessModelIds?.includes(6) && (
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Enter Other Business Model"
                                variant="outlined"
                                margin="normal"
                                type="text"
                                name="otherBusinessModel"
                                value={formData.otherBusinessModel}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!errors.otherBusinessModel}
                                helperText={errors.otherBusinessModel}
                                disabled={isLockedField("otherBusinessModel")}
                                inputProps={{ maxLength: 100 }}
                                InputProps={{
                                    classes: {
                                        disabled: classes.notSoLight
                                    }
                                }}
                            />
                        </Grid>
                    )}

                    {/* Product Type Other */}
                    {formData?.productTypeIds?.includes(10) && (
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Enter Other Product Type"
                                variant="outlined"
                                margin="normal"
                                type="text"
                                name="otherProductType"
                                value={formData.otherProductType}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!errors.otherProductType}
                                helperText={errors.otherProductType}
                                disabled={isLockedField("otherProductType")}
                                inputProps={{ maxLength: 100 }}
                                InputProps={{
                                    classes: {
                                        disabled: classes.notSoLight
                                    }
                                }}
                            />
                        </Grid>
                    )}

                    {/* Right Column */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            label="Select Marketplace"
                            variant="outlined"
                            margin="normal"
                            name="marketplaceIds"
                            value={formData.marketplaceIds || []}
                            onChange={handleMultiModelChange}
                            onBlur={handleBlur}
                            SelectProps={{ multiple: true }}
                            error={!!errors.marketplaceIds}
                            helperText={errors.marketplaceIds}
                            disabled={isLockedField("marketplaceIds")}
                            InputProps={{
                                classes: {
                                    disabled: classes.notSoLight
                                }
                            }}
                        >
                            {marketplaceOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Average Orders Per Day (number)"
                            variant="outlined"
                            margin="normal"
                            type="number"
                            name="averageOrdersPerDay"
                            value={formData.averageOrdersPerDay}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors.averageOrdersPerDay}
                            helperText={errors.averageOrdersPerDay}
                            onKeyDown={(e) => {
                                if (["e", "E", "+", "-", "."].includes(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                            disabled={isLockedField("averageOrdersPerDay")}
                            inputProps={{ min: 0 }}
                            InputProps={{
                                classes: {
                                    disabled: classes.notSoLight
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Average SKU Count (number)"
                            variant="outlined"
                            margin="normal"
                            type="number"
                            name="averageSkuCount"
                            value={formData.averageSkuCount}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors.averageSkuCount}
                            helperText={errors.averageSkuCount}
                            onKeyDown={(e) => {
                                if (["e", "E", "+", "-", "."].includes(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                            disabled={isLockedField("averageSkuCount")}
                            inputProps={{ min: 0 }}
                            InputProps={{
                                classes: {
                                    disabled: classes.notSoLight
                                }
                            }}
                        />
                    </Grid>

                    {/* Notes Field - FIXED SPACING */}
                    <Grid item xs={12} className="pt-0 mt-0">
                        <TextField
                            fullWidth
                            label="Notes / Other Information"
                            variant="outlined"
                            margin="normal"
                            name="otherInformation"
                            value={formData.otherInformation}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isLockedField("otherInformation")}
                            multiline
                            maxRows={4}
                            inputProps={{ maxLength: 1000 }}
                            InputProps={{
                                classes: {
                                    disabled: classes.notSoLight
                                }
                            }}
                        />
                    </Grid>
                </Grid>

                {/* Store Information Header */}
                <Typography className="flexSpaceStart mt-3">
                    <Typography variant="h6">Store Information </Typography>
                        <Tooltip title="Add new store" arrow>
                            <Button
                                size="small"
                                disableElevation
                                variant="contained"
                                color="primary"
                                onClick={handleAddNewStore}
                                className='ml-2 cus-seconday-bg-btn btn'
                            >
                                Add New
                            </Button>
                        </Tooltip>
                </Typography>
                <div><span className="infoText mt-1">At least one store is required. You can add multiple stores.</span></div>

                {/* Store List */}
                {errors.storeList && <Typography color="error">{errors.storeList}</Typography>}
                {formData.storeList && formData.storeList.length > 0 &&
                    formData.storeList.map((store, index) => (
                        <Grid container spacing={2} className="dis-row" key={store.storeIndexId}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Seller Store Name"
                                    variant="outlined"
                                    margin="normal"
                                    name="sellerStoreName"
                                    value={store.sellerStoreName}
                                    onChange={(e) => handleStoreChange(e, index)}
                                    onBlur={handleBlur}
                                    disabled={isExistingStore(store)}
                                    InputProps={{
                                        classes: {
                                            disabled: classes.notSoLight
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Website URL/s"
                                    variant="outlined"
                                    margin="normal"
                                    name="websiteUrl"
                                    value={store.websiteUrl}
                                    onChange={(e) => handleStoreChange(e, index)}
                                    onBlur={handleBlur}
                                    disabled={isExistingStore(store)}
                                    InputProps={{
                                        classes: {
                                            disabled: classes.notSoLight
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Seller Logo URL/s"
                                    variant="outlined"
                                    margin="normal"
                                    name="sellerLogoUrl"
                                    value={store.sellerLogoUrl}
                                    onChange={(e) => handleStoreChange(e, index)}
                                    disabled={isExistingStore(store)}
                                    InputProps={{
                                        classes: {
                                            disabled: classes.notSoLight
                                        }
                                    }}
                                />
                            </Grid>

                            {index > 0 && !isExistingStore(store) && (
                                <Grid item xs={12} md={1} className="d-flex align-items-center">
                                    <IconButton color="primary" aria-label="Delete store" component="label" onClick={() => handleDeleteStore(index)}>
                                        <Tooltip title="Delete store" arrow>
                                            <DeleteIcon />
                                        </Tooltip>
                                    </IconButton>
                                </Grid>
                            )}
                        </Grid>
                    ))}
            </Paper>
            <div className='flexPayment mt-2'>
                {!isViewUserRequest ? <Typography component="em">* Please complete the form to finalize your onboarding process</Typography> : <Typography component="em"></Typography>}
                <div className="mt-3 alignItemsCenter">
                    <Button disabled={activeStep === 0} onClick={handleBack} disableElevation>
                        Back
                    </Button>
                    <Button
                        disableElevation
                        variant="contained"
                        color="primary"
                        onClick={handleSaveAndNext}
                        className='ml-2 cus-seconday-bg-btn btn'
                    >
                        Save & Next
                    </Button>
                </div>
            </div>
        </>
    );
}

AdditionalInformation.propTypes = {
    isViewUserRequest: PropTypes.bool,
    isApprovedSeller: PropTypes.bool,
    activeStep: PropTypes.number,
    handleBack: PropTypes.func,
    handleNext: PropTypes.func,
    data: PropTypes.object
};